import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import {
	COMMON_ROUTE,
	TermsAndConditionsAction,
	TermsAndConditionsConfigService,
	TermsAndConditionsSelector,
} from "@obg/ngx.common";
import { AuthSelector, Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { RouteAction, RouterState, RouteSelector } from "@obg/ngx.router";
import { ContentSectionGroupParams } from "@obg/ngx.ui.core";
import { ignoreNil } from "@obg/rxjs";
import { merge, Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";

import { TERMS_AND_CONDITIONS_ROUTES } from "./terms-and-conditions.const";

@Component({
	selector: "obg-m-terms-and-conditions-container",
	templateUrl: "./terms-and-conditions.container.html",
	styleUrls: ["./terms-and-conditions.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class TermsAndConditionsContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-terms-and-conditions") class = true;

	termsAndConditionsHistoryEnabled = this.termsAndConditionsConfigService.isHistoryEnabled();

	selectedIndex = 0;
	jurisdiction: Jurisdiction | undefined;
	data: { title: string; document: string } | undefined;

	versionHistory$ = this.store.select(this.termsAndConditionsSelector.getHistory());
	latestVersion$ = this.store.select(this.termsAndConditionsSelector.getLatestVersion());
	isAuthenticated$ = this.store.select(AuthSelector.isAuthenticated);

	private readonly TERMS_AND_CONDITIONS_ROUTES = [TERMS_AND_CONDITIONS_ROUTES[0], TERMS_AND_CONDITIONS_ROUTES[1]];

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<RouterState>,
		private termsAndConditionsConfigService: TermsAndConditionsConfigService,
		private termsAndConditionsSelector: TermsAndConditionsSelector,
		private termsAndConditionsAction: TermsAndConditionsAction,
		private changeDetector: ChangeDetectorRef
	) {}

	ngOnInit() {
		if (this.termsAndConditionsHistoryEnabled) {
			this.store.dispatch(this.termsAndConditionsAction.getLatestVersion());
		}

		const jurisdiction$ = this.store.select(JurisdictionSelector.get).pipe(
			ignoreNil(),
			tap((jurisdiction) => (this.jurisdiction = jurisdiction))
		);

		const tabs$ = this.store.pipe(RouteSelector.getName$).pipe(
			tap((name: string) => {
				name === COMMON_ROUTE.termsAndConditions.root && this.jurisdiction === "Lotba"
					? this.navigateTo(this.TERMS_AND_CONDITIONS_ROUTES[0])
					: (this.selectedIndex = this.TERMS_AND_CONDITIONS_ROUTES.indexOf(name));
			})
		);

		const fetchHistory$ = this.store.select(AuthSelector.isAuthenticated).pipe(
			filter((isAuthenticated) => isAuthenticated),
			take(1),
			tap(() => {
				if (this.termsAndConditionsHistoryEnabled) {
					this.store.dispatch(this.termsAndConditionsAction.getHistory());
				}
			})
		);

		const routeData$ = this.store
			.select<({ title: string } & ContentSectionGroupParams) | undefined>(RouteSelector.getData)
			.pipe(tap((data) => (this.data = data)));

		this.data$$ = merge(routeData$, fetchHistory$, jurisdiction$, tabs$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	selectTab(tabIndex: number) {
		const route = this.TERMS_AND_CONDITIONS_ROUTES[tabIndex];
		this.navigateTo(route);
	}

	private navigateTo(route: string) {
		this.store.dispatch(RouteAction.navigate(route, undefined, { replaceUrl: true }));
	}
}
